import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Fire, TrendUp, TrendDown, Sparkle, BookOpen } from "@phosphor-icons/react";

export default function DashboardPage() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [results, setResults] = useState([]);
    const [courses, setCourses] = useState([]);

    useEffect(() => {
        Promise.all([
            api.get("/dashboard/stats").then(r => r.data),
            api.get("/results").then(r => r.data),
            api.get("/courses").then(r => r.data),
        ]).then(([s, r, c]) => { setStats(s); setResults(r); setCourses(c); });
    }, []);

    return (
        <div className="min-h-screen" data-testid="dashboard-page">
            <Navbar />
            <section className="mx-auto max-w-7xl px-6 py-12">
                <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Your Study Room</div>
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                    <h1 className="font-serif text-5xl md:text-6xl text-ink leading-none">
                        Welcome, <span className="italic font-light">{user?.name?.split(" ")[0]}</span>.
                    </h1>
                    <Link to="/courses">
                        <Button className="bg-ink text-bone hover:bg-ink/90 rounded-sm h-11" data-testid="btn-browse-courses">
                            Start a new test <ArrowRight size={16} className="ml-2"/>
                        </Button>
                    </Link>
                </div>

                {/* Metrics */}
                <div className="grid md:grid-cols-4 gap-4 mt-10">
                    <Stat label="Tests attempted" value={stats?.total_tests ?? 0} icon={<BookOpen size={18} weight="duotone"/>} testid="stat-total"/>
                    <Stat label="Average accuracy" value={`${stats?.avg_score ?? 0}%`} icon={<Sparkle size={18} weight="duotone" className="text-gold"/>} testid="stat-avg"/>
                    <Stat label="Courses practiced" value={stats?.courses_practiced ?? 0} icon={<Fire size={18} weight="duotone"/>} testid="stat-courses"/>
                    <Stat label="Plan" value={user?.is_premium ? "Premium" : "Free"} icon={<Sparkle size={18} weight="duotone" className={user?.is_premium ? "text-gold" : ""}/>} testid="stat-plan"/>
                </div>

                {/* Strengths / Weaknesses */}
                <div className="grid md:grid-cols-2 gap-6 mt-6">
                    <div className="border border-ink/15 rounded-sm p-6 bg-bone" data-testid="strengths-card">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-emerald-800 mb-4">
                            <TrendUp size={14}/> Strengths
                        </div>
                        {stats?.strengths?.length ? (
                            <ul className="space-y-3">
                                {stats.strengths.map(s => (
                                    <li key={s.topic} className="flex items-center justify-between">
                                        <span className="text-ink">{s.topic}</span>
                                        <span className="font-mono text-sm text-emerald-800">{s.percent}%</span>
                                    </li>
                                ))}
                            </ul>
                        ) : <div className="text-sm text-ink/50">Attempt a test to see your strengths.</div>}
                    </div>
                    <div className="border border-ink/15 rounded-sm p-6 bg-bone" data-testid="weaknesses-card">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-oxblood mb-4">
                            <TrendDown size={14}/> Focus areas
                        </div>
                        {stats?.weaknesses?.length ? (
                            <ul className="space-y-3">
                                {stats.weaknesses.map(s => (
                                    <li key={s.topic} className="flex items-center justify-between">
                                        <span className="text-ink">{s.topic}</span>
                                        <span className="font-mono text-sm text-oxblood">{s.percent}%</span>
                                    </li>
                                ))}
                            </ul>
                        ) : <div className="text-sm text-ink/50">Attempt more tests for personalised guidance.</div>}
                    </div>
                </div>

                {/* Recent results */}
                <div className="mt-10">
                    <div className="flex items-center justify-between mb-4">
                        <div className="font-serif text-2xl text-ink">Recent tests</div>
                        <Link to="/results" className="text-xs underline underline-offset-4 decoration-gold decoration-2 text-ink" data-testid="link-all-results">See all</Link>
                    </div>
                    {results.length === 0 ? (
                        <div className="border border-dashed border-ink/20 rounded-sm p-10 text-center bg-bone" data-testid="no-results">
                            <div className="text-ink/60">No tests yet. Your first test is a click away.</div>
                            <Link to="/courses"><Button className="mt-4 bg-ink text-bone hover:bg-ink/90 rounded-sm" data-testid="btn-first-test">Choose a course</Button></Link>
                        </div>
                    ) : (
                        <div className="divide-y divide-ink/10 border border-ink/15 rounded-sm bg-bone">
                            {results.slice(0, 6).map(r => (
                                <Link key={r.id} to={`/results/${r.id}`} data-testid={`recent-result-${r.id}`} className="flex items-center justify-between px-6 py-4 hover:bg-parchment/50 transition-colors">
                                    <div>
                                        <div className="font-serif text-lg text-ink">{r.course_name}</div>
                                        <div className="text-xs text-ink/50 uppercase tracking-widest">{r.test_type} · {new Date(r.created_at).toLocaleDateString()}</div>
                                    </div>
                                    <div className="flex items-center gap-6">
                                        <div className="text-right">
                                            <div className={`font-serif text-xl ${r.percent >= 75 ? "text-emerald-800" : r.percent >= 50 ? "text-gold-dark" : "text-oxblood"}`}>{r.percent}%</div>
                                            <div className="text-[10px] uppercase tracking-widest text-ink/50">{r.score}/{r.total}</div>
                                        </div>
                                        <ArrowRight size={16} className="text-ink/40"/>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>

                {/* Quick launch */}
                <div className="mt-14">
                    <div className="font-serif text-2xl text-ink mb-4">Continue with a course</div>
                    <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {courses.slice(0, 8).map(c => (
                            <Link key={c.id} to={`/courses/${c.id}`} data-testid={`quick-course-${c.id}`} className="border border-ink/15 bg-bone rounded-sm p-5 hover:border-ink/40 hover:-translate-y-0.5 transition-[transform,border-color] duration-200">
                                <div className="h-1 w-8 rounded-sm mb-3" style={{ backgroundColor: c.color }}></div>
                                <div className="font-serif text-lg text-ink">{c.name}</div>
                                <div className="text-xs text-ink/60 mt-1">{c.category}</div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    );
}

function Stat({ label, value, icon, testid }) {
    return (
        <div className="border border-ink/15 rounded-sm p-5 bg-bone" data-testid={testid}>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-ink/50">{icon} {label}</div>
            <div className="font-serif text-3xl text-ink mt-2">{value}</div>
        </div>
    );
}
